/**
 * The undo toast (2.L deliverable 12): UI r4's `Toast` with a ten second countdown, the label of
 * the change and an Undo button. One `undo.apply` at a time (the button is busy while it is in
 * flight); a refusal or a missing answer is said plainly and nothing is retried.
 */
import { undoApply } from '@codex/contracts';
import { Toast } from '@codex/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState, useSyncExternalStore } from 'react';
import { api } from '../auth/client.js';
import {
  isRefusal,
  labelFor,
  refusalText,
  UNDO_MS,
  UNDONE_TEXT,
  type UndoOffer,
} from './core/offers.js';
import { offerStore } from './store.js';

/** Renders while an offer shows; the query client is only needed then. */
export function UndoToastHost({ notify }: { notify: (text: string) => void }) {
  const { current } = useSyncExternalStore(offerStore.subscribe, offerStore.get, offerStore.get);
  if (current === null) return null;
  return <OfferToast key={current.batchId} offer={current} notify={notify} />;
}

function OfferToast({ offer, notify }: { offer: UndoOffer; notify: (text: string) => void }) {
  const client = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);
  const inFlight = useRef(false);

  const undo = useCallback(
    async (batchId: string) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setBusyId(batchId);
      try {
        await api(undoApply, { params: { id: batchId } });
        offerStore.close(batchId);
        notify(UNDONE_TEXT);
        await client.invalidateQueries();
      } catch (error) {
        if (isRefusal(error)) offerStore.close(batchId);
        notify(refusalText(error));
      } finally {
        inFlight.current = false;
        setBusyId(null);
      }
    },
    [client, notify],
  );

  const { batchId, labelKey } = offer;
  return (
    <Toast
      duration={UNDO_MS}
      onExpire={() => offerStore.close(batchId)}
      testId="undo.toast.root"
      testIds={{ action: 'undo.toast.button' }}
      action={{ label: 'Undo', busy: busyId === batchId, onClick: () => void undo(batchId) }}
    >
      {labelFor(labelKey)}
    </Toast>
  );
}
